import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApiError } from "../hooks/useApiError";
import { LangToggle, ThemeToggle } from "../components/controls/Toggles";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";
import { PasswordInput } from "../components/neu/PasswordInput";

export default function PlatformLoginPage() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const describe = useApiError();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const profile = await signIn(form.email.trim(), form.password);
      if (!["super_admin", "platform_admin"].includes(profile.role)) {
        setError(t("auth.invalid_credentials"));
        return;
      }
      navigate("/platform", { replace: true });
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-5 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="neu flex h-12 w-12 items-center justify-center rounded-2xl p-0">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <p className="text-xl font-black">{t("platform.title")}</p>
          </div>
          <div className="flex gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </div>
        <NeuCard className="animate-rise-in p-8" data-testid="platform-login-card">
          <h1 className="text-2xl font-black tracking-tight">{t("platform.login")}</h1>
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <NeuInput
              data-testid="platform-email"
              type="email"
              dir="ltr"
              required
              placeholder={t("auth.email")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <PasswordInput
              data-testid="platform-password"
              required
              placeholder={t("auth.password")}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            {error ? (
              <p data-testid="platform-login-error" className="text-sm font-semibold text-destructive">
                {error}
              </p>
            ) : null}
            <NeuButton variant="primary" type="submit" data-testid="platform-submit" disabled={busy} className="w-full py-4">
              {busy ? t("app.loading") : t("auth.signIn")}
            </NeuButton>
          </form>
          <Link to="/forgot-password" className="mt-5 block text-center text-xs font-semibold text-primary" data-testid="platform-forgot-link">{t("reset.forgotLink")}</Link>
        </NeuCard>
      </div>
    </div>
  );
}
